/**
 * The end-of-contract RETRY guard.
 *
 * A retry resends the FROZEN dates of the batch that was submitted, not the
 * live picker value. If midnight has since advanced the Brussels minimum past
 * a frozen date, resending it would only earn a backend
 * `EFFECTIVE_DATE_TOO_SOON` — so the retry is blocked and the user must go
 * back and re-pick.
 *
 * ADMINS ARE EXEMPT. After portals-backend #476 the server skips both the
 * minimum-date and the first-of-month checks for an admin (resolved from the
 * JWT via `isAdminContact`, never from the request body). An admin's frozen
 * date is therefore legitimate however early it is, and a retry must not be
 * blocked on a rule that no longer applies to them.
 *
 * `minDate` is injected rather than computed here: the caller owns the single
 * definition of the minimum (`computeEocDate()` — first day of the
 * month-after-next in Europe/Brussels), and passing it in keeps this predicate
 * pure and clock-free so it can be unit-tested in both directions.
 *
 * All comparisons are plain `yyyy-MM-dd` string comparisons — lexicographic
 * order equals chronological order for that format, so there is no Date
 * parsing and no timezone pitfall.
 */
export interface EocRetryGuardInput {
  /** FE affordance only — the authoritative admin check is server-side. */
  isAdmin: boolean;
  /** Does the retry still have a commodity contract left to resend? */
  hasPendingCommodity: boolean;
  /** Does the retry still have a flex contract left to resend? */
  hasPendingFlex: boolean;
  /** Frozen commodity effectiveDate the retry would resend (yyyy-MM-dd). */
  frozenCommodityDate: string;
  /** Frozen flex effectiveDate the retry would resend (yyyy-MM-dd). */
  frozenFlexDate: string;
  /** Current minimum allowed end date (yyyy-MM-dd), Brussels-derived. */
  minDate: string;
}

export const isEocRetryBlocked = ({
  isAdmin,
  hasPendingCommodity,
  hasPendingFlex,
  frozenCommodityDate,
  frozenFlexDate,
  minDate,
}: EocRetryGuardInput): boolean => {
  if (isAdmin) return false;
  return (
    (hasPendingCommodity && frozenCommodityDate < minDate) ||
    (hasPendingFlex && frozenFlexDate < minDate)
  );
};
